import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck } from "lucide-react";

/**
 * LegalFooter
 * Shared footer used on legal + pricing pages.
 * Mobile-first: collapses to single column at small breakpoints.
 */
export default function LegalFooter() {
  const year = new Date().getFullYear();
  return (
    <footer className="bg-slate-50 border-t border-slate-200" data-testid="legal-footer">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12 lg:py-16">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          <div className="col-span-2 md:col-span-1">
            <Link to="/" className="flex items-center gap-2 group">
              <div className="h-8 w-8 rounded-lg bg-blue-600 flex items-center justify-center">
                <ShieldCheck className="h-5 w-5 text-white" strokeWidth={2.5} />
              </div>
              <span className="font-display text-lg font-semibold tracking-tight">
                AgentFlow<span className="text-blue-600">.</span>
              </span>
            </Link>
            <p className="mt-3 text-sm text-slate-600 leading-relaxed max-w-xs">
              AI-powered regulatory compliance for small and medium businesses.
            </p>
          </div>

          <FooterCol
            title="Product"
            links={[
              { to: "/", label: "Overview" },
              { to: "/pricing", label: "Pricing" },
              { to: "/register", label: "Start Free" },
              { to: "/login", label: "Sign in" },
            ]}
          />
          <FooterCol
            title="Legal"
            links={[
              { to: "/terms", label: "Terms of Service" },
              { to: "/privacy", label: "Privacy Policy" },
              { to: "/refund", label: "Refund Policy" },
            ]}
          />
          <FooterCol
            title="Contact"
            links={[
              { href: "mailto:support@agentflow.ventures", label: "support@agentflow.ventures" },
              { href: "mailto:hello@agentflow.ventures", label: "hello@agentflow.ventures" },
            ]}
          />
        </div>

        <div className="mt-12 pt-8 border-t border-slate-200 flex flex-col sm:flex-row gap-4 sm:justify-between items-start sm:items-center text-xs text-slate-500">
          <p>© {year} AgentFlow Ventures Ltd. All rights reserved. Incorporated in the Cayman Islands.</p>
          <p className="text-slate-400">
            AgentFlow provides informational compliance tooling and does not constitute legal advice.
          </p>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, links }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-[0.18em] font-semibold text-slate-500 mb-3">{title}</p>
      <ul className="space-y-2 text-sm">
        {links.map((l) => (
          <li key={l.label}>
            {l.to ? (
              <Link to={l.to} className="text-slate-700 hover:text-slate-900 transition">{l.label}</Link>
            ) : (
              <a href={l.href} className="text-slate-700 hover:text-slate-900 transition break-all">{l.label}</a>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
